import { Nullable } from '@/models/Model';
import { TokenConfig, TokenInstance } from './types';
import {
  getTokenAttrs,
  TOKEN_KEY_ATTR,
  updateTokenElement,
} from './utils/token-element';
import { appendTokenizedContent } from './utils/tokenize';

export const renderValue = (
  root: HTMLElement,
  value: string,
  config: TokenConfig,
): TokenInstance[] => {
  root.textContent = '';
  const { tokens } = appendTokenizedContent(root, value, config);
  return tokens;
};

const collectEditableTextNodes = (root: HTMLElement): Text[] => {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node =>
      node.parentElement?.hasAttribute(TOKEN_KEY_ATTR)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  let current = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }
  return nodes;
};

const renumberTokens = (
  root: HTMLElement,
  config: TokenConfig,
): TokenInstance[] => {
  const tokens: TokenInstance[] = [];
  const counter: Record<string, number> = {};
  const elements = root.querySelectorAll<HTMLElement>(`[${TOKEN_KEY_ATTR}]`);

  elements.forEach(element => {
    const { key, match } = getTokenAttrs(element);
    const definition = config[key];
    if (!definition) return;

    const index = (counter[key] ?? 0) + 1;
    counter[key] = index;
    updateTokenElement(element, definition.render({ index, match }));
    tokens.push({ key, index, match });
  });

  return tokens;
};

export const onChangeContentEditable = (
  root: HTMLElement,
  config: TokenConfig,
): {
  tokens: TokenInstance[];
  lastInserted: Nullable<HTMLElement>;
} => {
  let lastInserted: Nullable<HTMLElement> = null;

  collectEditableTextNodes(root).forEach(textNode => {
    const content = textNode.textContent ?? '';
    if (!content) return;

    const fragment = document.createDocumentFragment();
    const { tokens, lastTokenElement } = appendTokenizedContent(
      fragment,
      content,
      config,
    );
    if (tokens.length === 0) return;

    lastInserted = lastTokenElement ?? lastInserted;
    textNode.replaceWith(fragment);
  });

  const tokens = renumberTokens(root, config);
  return { tokens, lastInserted };
};
